
import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowRight, LayoutDashboard } from 'lucide-react';
import { Page } from '../types';

interface HeaderProps {
  currentPage: Page;
  onNavigate: (page: Page) => void;
  onContactClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ currentPage, onNavigate, onContactClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Hantera scroll-tillstånd för headerns utseende
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lås bakgrundsscroll när menyn är öppen
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.touchAction = 'auto';
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.touchAction = 'auto';
    };
  }, [isMenuOpen]);

  const navLinks: { name: string; id: Page }[] = [
    { name: 'Plattform', id: 'home' },
    { name: 'Tjänster', id: 'services' },
    { name: 'Prisplaner', id: 'pricing' },
    { name: 'Expertis', id: 'about' },
    { name: 'Kontakt', id: 'contact' },
  ];

  const handleLinkClick = (page: Page) => {
    onNavigate(page);
    setIsMenuOpen(false);
  };

  const toggleMenu = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-[1000] transition-all duration-500 ease-in-out ${
        isScrolled || currentPage === 'dashboard' || isMenuOpen
          ? 'py-3 bg-white shadow-xl border-b border-slate-100' 
          : 'py-8 bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6 flex items-center justify-between relative z-[1010]">
        
        {/* Logotyp */}
        <a 
          href="/"
          onClick={(e) => {
            e.preventDefault();
            onNavigate('home');
            setIsMenuOpen(false);
          }} 
          className="flex items-center gap-3 outline-none group text-left relative z-[1020]"
        >
          <div className="flex-shrink-0 relative w-9 h-9 flex items-center justify-center">
            <svg width="34" height="34" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="transform group-hover:rotate-12 transition-transform duration-500">
              <path d="M14 6C18.4183 6 22 9.58172 22 14" stroke="#4285F4" strokeWidth="3.5" strokeLinecap="round"/>
              <path d="M22 14C22 18.4183 18.4183 22 14 22" stroke="#EA4335" strokeWidth="3.5" strokeLinecap="round"/>
              <path d="M14 22C9.58172 22 6 18.4183 6 14" stroke="#FBBC05" strokeWidth="3.5" strokeLinecap="round"/>
              <path d="M6 14C6 9.58172 9.58172 6 14 6" stroke="#34A853" strokeWidth="3.5" strokeLinecap="round"/>
              <path d="M20 20L26 26" stroke="#4285F4" strokeWidth="3.5" strokeLinecap="round"/>
            </svg>
          </div>
          
          <div className="flex items-baseline">
            <span className="text-[20px] sm:text-[22px] font-black tracking-tighter text-slate-900">
              Sökoptimera
            </span>
            <span className="text-[20px] sm:text-[22px] font-bold text-slate-400">
              .com
            </span>
          </div>
        </a>

        {/* Desktop-navigation */}
        <nav className="hidden lg:flex items-center bg-slate-100/50 backdrop-blur-sm p-1.5 rounded-2xl border border-slate-200/50">
          {navLinks.map((link) => (
            <a 
              key={link.id} 
              href={link.id === 'home' ? '/' : `/${link.id}`}
              onClick={(e) => {
                e.preventDefault();
                handleLinkClick(link.id);
              }}
              className={`px-5 py-2 rounded-xl text-[13px] font-bold tracking-wide transition-all duration-300 outline-none relative ${
                currentPage === link.id 
                  ? 'bg-white text-indigo-600 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              {link.name}
            </a>
          ))}
          <div className="w-px h-4 bg-slate-200 mx-2"></div>
          <a 
            href="/dashboard"
            onClick={(e) => {
              e.preventDefault();
              handleLinkClick('dashboard');
            }}
            className={`px-5 py-2 rounded-xl text-[13px] font-bold tracking-wide transition-all duration-300 outline-none flex items-center gap-2 ${
              currentPage === 'dashboard' 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' 
                : 'text-indigo-600 hover:bg-indigo-50'
            }`}
          >
            <LayoutDashboard size={14} />
            Kundportal
          </a>
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:block">
          <button 
            onClick={onContactClick}
            className="group relative bg-[#0f172a] text-white px-8 py-4 rounded-2xl font-black text-sm hover:bg-slate-900 shadow-xl shadow-slate-200/50 transition-all active:scale-95 flex items-center gap-2 overflow-hidden"
          >
            <span className="relative z-10">Fri SEO-analys</span>
            <ArrowRight size={18} className="relative z-10 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* Mobil Hamburgarmeny-knapp (Alltid synlig vid scroll/öppning) */}
        <button 
          className={`lg:hidden relative z-[1100] p-4 rounded-2xl shadow-xl border transition-all active:scale-90 flex items-center justify-center touch-manipulation ${
            isMenuOpen 
              ? 'bg-slate-900 text-white border-slate-800' 
              : 'bg-white text-slate-900 border-slate-200 shadow-slate-200/50'
          }`} 
          onClick={toggleMenu}
          aria-label={isMenuOpen ? "Stäng meny" : "Öppna meny"}
        >
          {isMenuOpen ? <X size={28} strokeWidth={3} /> : <Menu size={28} strokeWidth={2.5} />}
        </button>
      </div>

      {/* Mobilmeny Overlay */}
      <div 
        className={`lg:hidden fixed inset-0 z-[1050] bg-white transition-all duration-500 ease-in-out transform ${
          isMenuOpen ? 'translate-y-0 opacity-100 visible' : '-translate-y-full opacity-0 invisible pointer-events-none'
        }`}
      >
        {/* Bakgrunds-dekor för menyn */}
        <div className="absolute inset-0 bg-blueprint opacity-40 pointer-events-none"></div>

        <div className="flex flex-col h-full pt-32 px-8 pb-12 overflow-y-auto overscroll-contain relative z-10">
          
          <div className="space-y-4">
            <p className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.4em] mb-6">Navigering</p>
            {navLinks.map((link) => (
              <a 
                key={link.id} 
                href={link.id === 'home' ? '/' : `/${link.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleLinkClick(link.id);
                }}
                className={`w-full text-4xl font-black text-left py-4 border-b border-slate-100 transition-colors flex items-center justify-between group ${
                  currentPage === link.id ? 'text-indigo-600' : 'text-slate-900'
                }`}
              >
                <span>{link.name}</span>
                <ArrowRight size={24} className={`transition-transform duration-300 ${currentPage === link.id ? 'translate-x-0 opacity-100' : '-translate-x-4 opacity-0'}`} />
              </a>
            ))}
            <a 
              href="/dashboard"
              onClick={(e) => {
                e.preventDefault();
                handleLinkClick('dashboard');
              }}
              className={`w-full text-4xl font-black text-left py-4 border-b border-slate-100 flex items-center justify-between transition-colors ${
                currentPage === 'dashboard' ? 'text-indigo-600' : 'text-slate-900'
              }`}
            >
              <span>Kundportal</span>
              <LayoutDashboard size={32} />
            </a>
          </div>
          
          <div className="mt-auto pt-12">
            <button 
              onClick={() => {
                onContactClick();
                setIsMenuOpen(false);
              }}
              className="w-full bg-indigo-600 text-white py-6 rounded-[2rem] font-black text-xl flex items-center justify-center gap-4 shadow-2xl shadow-indigo-100 transition-transform active:scale-95"
            >
              Boka Fri SEO-analys <ArrowRight size={24} />
            </button>
            
            <div className="mt-8 flex flex-col items-center gap-4">
              <button 
                onClick={() => setIsMenuOpen(false)}
                className="text-slate-400 font-bold text-sm uppercase tracking-widest flex items-center gap-2 hover:text-slate-600 transition-colors p-4"
              >
                <X size={20} strokeWidth={3} /> Stäng menyn
              </button>
              <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest">
                © 2026 Sökoptimera.com
              </p>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
